import { ComponentSelectDto, SubsystemSelectDto, TechnologySelectDto, TechnologyType } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { CountrySelector } from '@components/shared/country-selector.component';
import { ErrorMessage } from '@components/shared/error-message.component';
import { FormField } from '@components/shared/form-field.component';
import { FormLabel } from '@components/shared/form-label.component';
import { PopoverSelector } from '@components/shared/popover-selector.component';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@components/ui/alert-dialog';
import { Button as CustomButton } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Textarea } from '@components/ui/textarea';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import { useGetCategories } from '@queries/category.query';
import {
  useCreateTechnologySubsystem,
  useDeleteTechnologySubsystem,
  useUpdateTechnologySubsystem,
} from '@queries/technology-subsystems.query';
import { type SubsystemFormData, type SubsystemFormErrors, subsystemCreateSchema } from '@schemas/subsystem.schema';
import { mapToSelectOptions } from '@utils/select.util';
import { formatProper } from '@utils/text.util';
import { X } from 'lucide-react';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

interface TechnologySubsystemModalProps {
  isEdit: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  subsystem: SubsystemSelectDto | null;
  technology?: TechnologySelectDto;
}

interface ComponentFormData {
  id?: string;
  name: string;
  origin?: string;
  price?: number | string | null;
}

// Form data type that allows strings for price during typing
type SubsystemFormDataWithStringPrice = Omit<SubsystemFormData, 'components'> & {
  components: ComponentFormData[];
};

const defaultFormData: SubsystemFormDataWithStringPrice = {
  description: undefined,
  name: '',
  origin: undefined,
  technologyId: '',
  type: TechnologyType.SOFTWARE,
  categoryId: '',
  subcategoryId: '',
  components: [],
};

const emptyErrors: SubsystemFormErrors = { _errors: [] };

export const TechnologySubsystemModal: FunctionComponent<TechnologySubsystemModalProps> = (props) => {
  const { isEdit, isOpen, setIsOpen, technology, subsystem } = props;
  const [formData, setFormData] = useState<SubsystemFormDataWithStringPrice>(defaultFormData);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [errors, setErrors] = useState<SubsystemFormErrors>(emptyErrors);
  const { data: categories = [] } = useGetCategories();

  const categoryOptions = useMemo(
    () =>
      mapToSelectOptions(categories).map((category) => ({
        ...category,
        subcategories: mapToSelectOptions(category.subcategories),
      })),
    [categories],
  );

  const resetForm = useCallback(() => {
    setFormData({
      ...defaultFormData,
      technologyId: technology?.id || '',
    });
  }, [technology?.id]);

  useEffect(() => {
    if (isOpen) {
      // Reset errors when modal opens
      setErrors(emptyErrors);

      if (isEdit && subsystem) {
        setFormData({
          description: subsystem.description || undefined,
          name: subsystem.name || '',
          origin: subsystem.origin || undefined,
          technologyId: technology?.id || '',
          type: subsystem.type,
          categoryId: subsystem.category.id || '',
          subcategoryId: subsystem.subcategory.id || '',
          components: subsystem.components.map((component: ComponentSelectDto) => ({
            id: component.id,
            name: component.name,
            origin: component.origin || undefined,
            price: component.price || undefined,
          })),
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, isEdit, subsystem, resetForm, technology?.id]);

  const formatNumberWithCommas = (value: string | number | undefined): string => {
    if (value === undefined || value === null || value === '') return '';
    const number = value.toString().replace(/,/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const closeModal = () => {
    setIsOpen(false);
    setErrors(emptyErrors);
  };

  const handleFieldChange = <K extends keyof SubsystemFormDataWithStringPrice>(
    field: K,
    value: SubsystemFormDataWithStringPrice[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const addComponent = () => {
    setErrors((prev) => ({
      ...prev,
      components: undefined,
    }));

    setFormData((prev) => ({
      ...prev,
      components: [...prev.components, { name: '', origin: undefined, price: undefined }],
    }));
  };

  const updateComponent = (index: number, field: keyof ComponentFormData, value: string) => {
    setFormData((prev) => {
      const newComponents = [...prev.components];

      if (field === 'price') {
        if (value === '') {
          newComponents[index] = { ...newComponents[index], price: undefined };
        } else {
          const cleanedValue = value.replace(/,/g, '').trim();

          // Basic validation: only allow digits, one decimal point, and up to 2 decimal places
          if (!/^\d*\.?\d{0,2}$/.test(cleanedValue)) {
            return prev;
          }

          // Prevent multiple decimal points
          if ((cleanedValue.match(/\./g) || []).length > 1) {
            return prev;
          }

          // Allow intermediate states like "23." and store as string for now
          // Zod will handle the final validation and transformation
          if (cleanedValue === '.' || cleanedValue === '') {
            newComponents[index] = { ...newComponents[index], price: undefined };
          } else {
            // Store the raw input value, Zod will transform it properly
            newComponents[index] = { ...newComponents[index], price: cleanedValue };
          }
        }
      } else {
        newComponents[index] = { ...newComponents[index], [field]: value || undefined };
      }

      return { ...prev, components: newComponents };
    });
  };

  const removeComponent = (index: number) => {
    setErrors((prev) => ({
      ...prev,
      components: undefined,
    }));

    setFormData((prev) => {
      const newComponents = [...prev.components];
      newComponents.splice(index, 1);
      return { ...prev, components: newComponents };
    });
  };

  const { mutate: createTechnologySubsystem, isPending: isCreatePending } = useCreateTechnologySubsystem();
  const { mutate: updateTechnologySubsystem, isPending: isUpdatePending } = useUpdateTechnologySubsystem();
  const { mutate: deleteTechnologySubsystem, isPending: isDeletePending } = useDeleteTechnologySubsystem();

  const saveSubsystem = async () => {
    if (!technology) {
      errorToast({ description: 'Technology is required.' });
      return;
    }

    const validationResult = subsystemCreateSchema.safeParse(formData);

    if (!validationResult.success) {
      setErrors(validationResult.error.format());
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    setErrors(emptyErrors);

    if (isEdit && subsystem) {
      updateTechnologySubsystem({ ...validationResult.data, id: subsystem.id }, { onSuccess: () => closeModal() });
    } else {
      createTechnologySubsystem(validationResult.data, { onSuccess: () => closeModal() });
    }
  };

  const deleteSubsystem = () => {
    if (!isEdit || !subsystem || !technology) return;

    deleteTechnologySubsystem({ id: subsystem.id, technologyId: technology.id }, { onSuccess: () => closeModal() });
  };

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          loading: isCreatePending || isUpdatePending,
          onClick: saveSubsystem,
          children: isEdit ? 'Update' : 'Add',
        },
        leftButton: {
          loading: isDeletePending,
          onClick: isEdit ? () => setIsAlertOpen(true) : () => setIsOpen(false),
          children: isEdit ? 'Delete' : 'Cancel',
          variant: isEdit ? 'destructive' : 'secondary',
        },
      }}
      header={{ title: `${isEdit ? 'Update' : 'Add'} Subsystem` }}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      width="600px"
    >
      {isEdit && (
        <AlertDialog
          open={isAlertOpen}
          onOpenChange={setIsAlertOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the subsystem.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteSubsystem}
                variant="danger"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      <div className="flex flex-col items-center justify-center overflow-y-auto w-full gap-4">
        <div
          className="flex flex-col gap-4 w-full items-start"
          style={{ scrollbarGutter: 'stable' }}
        >
          <FormField>
            <FormLabel
              label="Name"
              isRequired
            />
            <Input
              name="name"
              hasError={!!errors.name}
              onChange={handleChange}
              placeholder="Subsystem name"
              value={formData.name}
            />
            <ErrorMessage zodError={errors.name} />
          </FormField>

          <div className="grid grid-cols-2 justify-between w-full gap-4">
            <FormField>
              <FormLabel
                label="Type"
                isRequired
                className="mb-1"
              />
              <Select
                value={formData.type || ''}
                onValueChange={(currentValue) => {
                  handleFieldChange('type', currentValue as TechnologyType);
                }}
              >
                <SelectTrigger
                  hasError={!!errors.type}
                  className={cn(
                    'w-full justify-between overflow-hidden bg-gray-100',
                    formData.type ? 'text-gray-600' : 'text-gray-400',
                  )}
                >
                  <span>
                    <SelectValue placeholder="Select..." />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.values(TechnologyType)
                      .filter((type) => type !== TechnologyType.HYBRID)
                      .map((type) => (
                        <SelectItem
                          key={type}
                          value={type}
                        >
                          <span>{formatProper(type)}</span>
                        </SelectItem>
                      ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <ErrorMessage zodError={errors.type} />
            </FormField>

            <FormField>
              <FormLabel
                label="Country of Origin"
                className="mb-1"
              />
              <CountrySelector
                name="origin"
                onChange={handleChange}
                value={formData.origin}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 justify-between w-full gap-4">
            <FormField>
              <PopoverSelector
                emptyMessage="No category found"
                isRequired
                hasError={!!errors.categoryId}
                label="Category"
                options={categoryOptions}
                placeholder="Select category"
                searchPlaceholder="Search category..."
                setValue={(id) => {
                  handleFieldChange('categoryId', id);
                  handleFieldChange('subcategoryId', '');
                }}
                value={formData.categoryId}
              />
              <ErrorMessage zodError={errors.categoryId} />
            </FormField>

            <FormField>
              <PopoverSelector
                disabled={!formData.categoryId}
                emptyMessage="No subcategory found"
                isRequired
                hasError={!!errors.subcategoryId}
                label="Subcategory"
                options={mapToSelectOptions(
                  categoryOptions.find((category) => category.id === formData.categoryId)?.subcategories || [],
                )}
                placeholder="Select subcategory"
                searchPlaceholder="Search subcategory..."
                setValue={(id) => handleFieldChange('subcategoryId', id)}
                value={formData.subcategoryId}
              />
              <ErrorMessage zodError={errors.subcategoryId} />
            </FormField>
          </div>

          <FormField>
            <FormLabel
              label="Description"
              className="mb-1"
            />
            <Textarea
              className="flex h-28 w-full rounded-lg bg-gray-100 p-3"
              hasError={!!errors.description}
              id="description"
              name="description"
              onChange={handleChange}
              value={formData.description || ''}
            />
          </FormField>

          {formData.components.length > 0 && (
            <div className="flex flex-col gap-2 w-full">
              <BlueLabel text="Components" />
              <div className="flex flex-col gap-4 w-full">
                {formData.components.map((component, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex flex-row items-start rounded-md border border-gray-200',
                      Object.keys(errors.components?.[index] || {}).length > 0 && 'border-red-500',
                    )}
                  >
                    <div className="flex flex-col gap-2 p-4 pr-0 w-full">
                      <FormField>
                        <FormLabel
                          label="Name"
                          isRequired
                          className="mb-1"
                        />
                        <Input
                          className="w-full"
                          hasError={!!errors.components?.[index]?.name}
                          onChange={(e) => updateComponent(index, 'name', e.target.value)}
                          placeholder="Enter component name"
                          value={component.name}
                        />
                        <ErrorMessage zodError={errors.components?.[index]?.name} />
                      </FormField>

                      <div className="flex gap-4 w-full">
                        <FormField className="w-1/2">
                          <FormLabel label="Country of Origin" />
                          <CountrySelector
                            name={`component-${index}-origin`}
                            onChange={(e) => updateComponent(index, 'origin', e.target.value)}
                            value={component.origin}
                          />
                        </FormField>

                        <FormField className="w-1/2">
                          <FormLabel label="Price per Component" />
                          <div className="relative w-full">
                            <Input
                              className="flex h-10 w-full rounded-lg p-3 pl-6 pr-12"
                              id={`component-${index}-price`}
                              hasError={!!errors.components?.[index]?.price}
                              inputMode="decimal"
                              name={`component-${index}-price`}
                              onChange={(e) => updateComponent(index, 'price', e.target.value)}
                              pattern="[0-9]*\.?[0-9]*"
                              type="text"
                              value={
                                component.price !== undefined && component.price !== null ? String(component.price) : ''
                              }
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                              $
                            </span>
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                              USD
                            </span>
                          </div>
                          <ErrorMessage zodError={errors.components?.[index]?.price} />
                        </FormField>
                      </div>
                    </div>
                    <div
                      onClick={() => removeComponent(index)}
                      className=" text-[#1F1C1AB2] hover:text-destructive cursor-pointer p-2"
                    >
                      <X size={16} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <CustomButton
            onClick={addComponent}
            variant="link"
          >
            + Add Component
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};
